import type { ConversationMessage, PlaceholderAnalysis } from '@/features/AI-documents/types/docGen.types';
import type { ChatFlow, ChatFlowContext, ChatFlowEventResult } from '@/features/startTask/preset_task/shared/preset/chatflows/types';

/** 결의 종류 3택 — 문서는 [[if include_md_appointment]] / [[if include_md_resignation]] 로 해당 결의만 남긴다. */
export const MD_ACTION_OPTIONS = [
  { key: 'appoint', label: '(1) Appointment of MD', desc: 'Resolution to appoint a new Managing Director', icon: 'mdi-account-plus-outline' },
  { key: 'withdraw', label: '(2) Withdrawal of MD', desc: 'Resolution for the resignation of the Managing Director', icon: 'mdi-account-minus-outline' },
  { key: 'both', label: '(3) Appointment & Withdrawal of MD', desc: 'Both resolutions on the same copy', icon: 'mdi-account-switch-outline' },
];

/**
 * Appoint / Withdraw MD (B32115) chatflow — withdrawDirectorFlow 와 같은 단계(제목 → 회사 → ✨ 인물 조회).
 *   패널이 새 MD 는 defaultRole CONTACT(글로벌 컨택트), 사임 MD 는 defaultRole MANAGING_DIRECTOR(회사 현직 MD)로 조회 요청.
 */
export const appointWithdrawMdFlow: ChatFlow = {
  id: 'sg-appoint-withdraw-md',
  taskCodeMatch: ['B32115'],
  categoryMatch: [
    'appoint/withdraw md',
    'appoint / withdraw md',
    'appoint withdraw md',
    'appoint/withdraw managing director',
    'managing director',
  ],

  getInitialMessages(preSet): ConversationMessage[] {
    const docCount = preSet.documents.length;
    return [
      {
        role: 'ai',
        content:
          `You selected **${preSet.task.TASK_NAME || 'Appoint / Withdraw MD'}** (Appointment & Resignation of Managing Director).\n\n` +
          (docCount > 0 ? `This preset includes ${docCount} document(s).\n\n` : '') +
          `First, enter a **Task Title**. Then choose the **company entity** to continue.\n\n` +
          `In the document, ✨ on the **new MD** looks up a **contact**; ✨ on the **resigning MD** looks up the company's **current MD**.`,
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'input-title',
      aiMessage: 'Please enter a Task Title.',
      widget: 'text-input',
      widgetConfig: {
        targetVarKey: '_taskTitle',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    {
      id: 'select-entity',
      aiMessage: 'Search and select the **Singapore company** whose Managing Director is being changed.',
      widget: 'entity-selector',
      widgetConfig: {
        targetVarKey: '_entity',
        targetEntityIndex: 0,
        analysis: {
          success: true,
          placeholder: '_entity',
          sourceResolver: 'ENTITY',
          officerRole: null,
          varKey: '_entity',
          varLabel: 'Entity',
          confidence: 1,
          message: 'Select entity (Appoint / Withdraw MD).',
          relatedFields: [],
          options: [],
        } as PlaceholderAnalysis,
      },
      awaitEvent: true,
    },
    {
      id: 'select-action',
      aiMessage: 'Which resolution applies to this situation?',
      widget: 'choice-buttons',
      widgetConfig: {
        targetVarKey: '_mdAction',
        targetEntityIndex: 0,
        choiceOptions: MD_ACTION_OPTIONS,
      },
      awaitEvent: true,
    },
    {
      id: 'confirm',
      aiMessage:
        'Fill in the selected resolution in the document — pick the **MD** with ✨, set the **effective date** and ' +
        '**resolution date**, then click **Create Task**.',
      widget: null,
      nextStep: null,
    },
    {
      id: 'officer-role',
      aiMessage: 'Select an officer role.',
      widget: 'officer-role-buttons',
      widgetConfig: {
        targetVarKey: '_officerRole',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    {
      id: 'officer-list',
      aiMessage: 'Select a person.',
      widget: 'officer-list',
      widgetConfig: {
        targetVarKey: '_officer',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    {
      id: 'officer-detail',
      aiMessage: 'Person selected.',
      widget: null,
      nextStep: 'confirm',
    },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    if (eventName === 'value-submit' && context.currentStepId === 'input-title') {
      const title = typeof payload === 'string' ? payload : payload?.value || payload;
      if (!title || !String(title).trim()) return {};
      return {
        messages: [{ role: 'user', content: String(title), timestamp: Date.now(), stepId: 1 }],
        formUpdates: { taskTitle: String(title).trim() },
        nextStepId: 'select-entity',
      };
    }

    if (eventName === 'entity-select' && context.currentStepId === 'select-entity') {
      const ent = payload || {};
      const entityCd = ent.entityCd || ent.icpCd || '';
      const entityName = ent.entityName || ent.companyName || 'Entity';
      if (!entityCd) return {};
      return {
        messages: [{ role: 'user', content: `Selected: **${entityName}**`, timestamp: Date.now(), stepId: 1 }],
        formUpdates: {
          entityCd,
          entityName,
          icpCd: entityCd,
          registrationNo: ent.registrationNo || ent.regNo || '',
        },
        nextStepId: 'select-action',
      };
    }

    // 결의 종류 선택 — 3택 단계에서 고르면 다음으로, 이후 다시 눌러도 값만 바꾼다.
    if (eventName === 'choice-select') {
      const opt = MD_ACTION_OPTIONS.find((o) => o.key === (typeof payload === 'string' ? payload : payload?.key));
      if (!opt) return {};
      return {
        messages: [{ role: 'user', content: `Selected: **${opt.label}**`, timestamp: Date.now(), stepId: 1 }],
        formUpdates: { mdAction: opt.key },
        ...(context.currentStepId === 'select-action' ? { nextStepId: 'confirm' } : {}),
      };
    }

    // 문서의 ✨ → 새 MD(CONTACT) / 사임 MD(MANAGING_DIRECTOR) 목록.
    if (eventName === 'request-person-lookup') {
      const ctx = payload || {};
      const entityCd = context.entityCd || context.formData.entityCd || '';
      if (!entityCd) {
        return {
          messages: [{ role: 'ai', content: 'Please select an entity first.', timestamp: Date.now(), stepId: 1, source: 'system' }],
        };
      }
      if (ctx.defaultRole) {
        return {
          messages: [{ role: 'user', content: `Looking up **${ctx.subjectLabel || ctx.fieldGroup || 'person'}**...`, timestamp: Date.now(), stepId: 1 }],
          nextStepId: 'officer-list',
          nextStepWidgetData: { entityCd, selectedRole: ctx.defaultRole },
        };
      }
      return {
        messages: [{ role: 'user', content: `Looking up **${ctx.fieldGroup || 'person'}**...`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-role',
      };
    }

    if (eventName === 'officer-role-select' && context.currentStepId === 'officer-role') {
      const role = payload;
      const entityCd = context.entityCd || context.formData.entityCd || '';
      return {
        messages: [{ role: 'user', content: `Role: **${role}**`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-list',
        nextStepWidgetData: { entityCd, selectedRole: role },
      };
    }

    if (eventName === 'officer-select' && context.currentStepId === 'officer-list') {
      const officer = payload || {};
      const officerName = officer.name || officer.contactName || 'Selected person';
      return {
        messages: [{ role: 'user', content: `Selected person: **${officerName}**`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-detail',
      };
    }

    if (eventName === 'officer-list-back' && context.currentStepId === 'officer-list') {
      return { nextStepId: 'officer-role' };
    }

    return {};
  },
};
